import React, { useMemo, useRef, useState, useEffect } from 'react'
import styled, { css, StyledProps } from 'styled-components'

import { useKey } from '../../../hooks'
import { IconMinus, IconAdd } from '../../iconography/Icons/Icons'
import { useTranslation } from 'react-i18next'

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: max-content auto max-content;
  align-items: center;
  border: 1px solid ${(props: StyledProps<{}>) => props.theme.color.onSurfaceLowEmphasys};
  border-radius: 8px;
  max-width: 100%;

  &[data-size='small'] {
    height: 32px;
    padding: 0 4px;
    width: 128px;
  }

  &[data-size='medium'] {
    width: 160px;
    height: 40px;
    padding: 0 4px;
  }

  &[data-size='large'] {
    padding: 0 8px;
    height: 48px;
    max-width: 100%;
  }

  ${props => props.theme.media.mobile`
    &[data-size='small'] {
        height: 32px;
        padding: 0 4px;
      }

      &[data-size='medium'] {
        height: 32px;
        padding: 0 4px;
      }
      &[data-size='large'] {
        height: 40px;
        padding: 0 5px;
      }
  `}
`

const Input = styled.input`
  flex-grow: 1;
  min-width: 50px;
  font-weight: 500;
  border: none;
  padding: 0;
  text-align: center;
  margin: 0 3px;

  &:focus {
    outline: none;
  }

  [data-size='small'] & {
    font-size: 14px;
  }

  [data-size='medium'] & {
    font-size: 16px;
  }

  [data-size='large'] & {
    font-size: 24px;
  }

  [data-disabled='true'] & {
    cursor: default;
  }

  ${props => props.theme.media.mobile`
    [data-size='small'] & {
      font-size: 16px;
    }

    [data-size='medium'] & {
      font-size: 16px;
    }

    [data-size='large'] & {
      font-size: 16px;
    }
  `}

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }

  &[type='number'] {
    appearance: textfield;
  }
`

const Button = styled.button`
  border: none;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin: 0;

  &:focus {
    outline: none;
  }

  height: 32px;
  width: 32px;
  border-radius: 4px;
  background-color: ${(props: StyledProps<{}>) => props.theme.color.primary25};
  transition: 0.3s background-color;
  padding: 0;

  &:hover,
  &:focus {
    background-color: ${(props: StyledProps<{}>) => props.theme.color.primary50};
  }

  [data-disabled='false'] &:focus {
    box-shadow: ${(props: StyledProps<{}>) => props.theme.shadow.focus};
  }

  [data-size='small'] & {
    width: 24px;
    height: 24px;
    padding: 1px;
  }

  [data-size='medium'] & {
    padding: 5px;
  }

  [data-disabled='true'] &,
  &:disabled {
    background-color: ${(props: StyledProps<{}>) => props.theme.color.backgroundBase};
    cursor: not-allowed;
  }

  ${props => props.theme.media.mobile`
    [data-size='medium'] & {
      width: 24px;
      height: 24px;
      padding: 1px;
    }
  `}

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`

const Icon = css`
  transition: 0.3s fill;
  width: 24px;
  height: 24px;
  fill: ${(props: StyledProps<{}>) => props.theme.color.primary600};

  [data-disabled='true'] &,
  button:disabled & {
    fill: ${(props: StyledProps<{}>) => props.theme.color.onSurfaceLowEmphasys};
  }
`
const Plus = styled(IconAdd)`
  ${Icon}
`

const Minus = styled(IconMinus)`
  ${Icon}
`

export type NumberInputProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'value'> & {
  value: number
  onChange: (value: number) => void
  onDecrease?: (value: number) => void
  onIncrease?: (value: number) => void
  size?: 'small' | 'medium' | 'large'
  defaultValue?: number
  step?: number
  inputStep?: number
  min?: number
  max?: number
  disabled?: boolean
  testId?: string
  selectTextOnFocus?: boolean
  name?: string
  showNothing?: boolean
  updateOnInputChange?: boolean
}

export const getStepDecimals = (step: number) => step.toString().split('.')[1]?.length || 0

export const numberToStep = (num: number, step: number) => {
  return Math.round(num / step) * step
}

export const NumberInput: React.FC<NumberInputProps> = ({
  onChange,
  onDecrease,
  onIncrease,
  step: stepProp,
  inputStep: inputStepProp,
  value: valueProp,
  min: minProp,
  max: maxProp,
  size = 'large',
  className,
  disabled,
  testId = 'input',
  selectTextOnFocus,
  name,
  showNothing,
  updateOnInputChange: updateOnInputChangeProp,
  ...divProps
}) => {
  const max = typeof maxProp === 'number' ? maxProp : Infinity
  const step = typeof stepProp === 'number' ? stepProp : 1
  const inputStep = typeof inputStepProp === 'number' ? inputStepProp : step
  const min = typeof minProp === 'number' ? minProp : step
  const value = typeof valueProp === 'number' ? valueProp : 0
  const updateOnInputChange = updateOnInputChangeProp ? updateOnInputChangeProp : inputStep === 1
  const isMaxReached = value >= max
  const isMinReached = value <= min
  const { t } = useTranslation('ui')
  const inputRef = useRef<HTMLInputElement>(null)
  const stepDecimals = useMemo(() => getStepDecimals(inputStep), [inputStep])
  const valueToFixed = (value: number) => (stepDecimals ? value.toFixed(stepDecimals) : value.toString())

  const [inputValue, setInputValue] = useState(valueToFixed(numberToStep(value, inputStep)))

  const changeValue = (newValue: string | number) => {
    const parsedValue = numberToStep(Math.min(Math.max(Number(newValue), min), max), inputStep)
    const toFixed = valueToFixed(parsedValue)
    if (valueProp !== parsedValue) {
      onChange(Number(toFixed))
    }

    setInputValue(toFixed)
  }

  useEffect(() => {
    if (valueProp !== Number(inputValue)) changeValue(value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueProp])

  const handleInputChange = e => {
    const val = e.target.value
    if (updateOnInputChange && val && val >= min) return changeValue(val)
    setInputValue(!val ? '' : val)
  }

  const handleBlur = () => {
    changeValue(inputValue)
  }

  const handleIncrease = () => {
    if (disabled) return
    onIncrease && onIncrease?.(value)
    const newValue = numberToStep(Math.min(value + step, max), inputStep)
    onChange(newValue)
    setInputValue(valueToFixed(newValue))
  }

  const handleDecrease = () => {
    if (disabled) return
    onDecrease && onDecrease(value)
    const newValue = numberToStep(Math.max(value - step, min), inputStep)
    onChange(newValue)
    setInputValue(valueToFixed(newValue))
  }

  useKey(inputRef, 'keydown', handleIncrease, 'ArrowUp')
  useKey(inputRef, 'keydown', handleDecrease, 'ArrowDown')

  return (
    <Wrapper
      data-test-id={'number-input'}
      data-size={size}
      className={className}
      data-disabled={disabled}
      {...divProps}
    >
      <Button
        onClick={handleDecrease}
        disabled={isMinReached || disabled}
        data-test-id={'decrement'}
        aria-label={t('NumberInput.remove', { count: step })}
      >
        <Minus />
      </Button>
      <Input
        aria-label={t('NumberInput.label')}
        value={showNothing ? '' : inputValue}
        onChange={handleInputChange}
        onFocus={selectTextOnFocus ? e => e.target.select() : undefined}
        onBlur={handleBlur}
        ref={inputRef}
        type="number"
        readOnly={disabled}
        data-test-id={testId}
        name={name}
        step={inputStep}
        role="number-input"
        min={min}
        max={max}
      />
      <Button
        onClick={handleIncrease}
        disabled={isMaxReached || disabled}
        data-test-id={'increment'}
        aria-label={t('add', { count: step })}
      >
        <Plus />
      </Button>
    </Wrapper>
  )
}
